<template>
  <v-dialog v-model="show_popup" persistent max-width="650">
    <v-card>
      <v-card-title class="red--text">
        <v-icon left color="red" large>mdi-bell-alert</v-icon>
        <span class="text-h5">Task Reminder</span>
        <v-spacer></v-spacer>
        <v-chip color="red" dark> {{ popup_tasks.length }} pending </v-chip>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text style="max-height: 450px; overflow-y: auto">
        <div v-if="overdueTasks.length > 0" class="mb-4">
          <div class="subtitle-2 red--text mb-2">
            <v-icon small color="red">mdi-alert</v-icon>
            Overdue Tasks
          </div>
          <v-list dense>
            <v-list-item
              v-for="task in overdueTasks"
              :key="'overdue-' + task.id"
              class="red lighten-5 mb-1 rounded"
              style="cursor: pointer"
              @click="openDetail(task)"
            >
              <v-list-item-icon>
                <v-icon
                  :color="
                    priorityColor(task.task ? task.task.priority : 'basic')
                  "
                >
                  mdi-alert-circle
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">
                  {{ task.task ? task.task.title : "" }}
                  <v-chip
                    v-if="task.days_delayed > 0"
                    x-small
                    color="deep-orange"
                    dark
                    class="ml-1"
                    >{{ task.days_delayed }} day{{
                      task.days_delayed > 1 ? "s" : ""
                    }}
                    delayed</v-chip
                  >
                </v-list-item-title>
                <v-list-item-subtitle>
                  Due: {{ task.log_date ? task.log_date.substr(0, 10) : "" }}
                  {{
                    task.task && task.task.scheduled_time
                      ? "at " + task.task.scheduled_time
                      : ""
                  }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn small color="success" @click="completeTask(task)">
                  <v-icon small left>mdi-check</v-icon>
                  Complete
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </div>

        <div v-if="todayTasks.length > 0">
          <div class="subtitle-2 primary--text mb-2">
            <v-icon small color="primary">mdi-calendar-today</v-icon>
            Today's Tasks
          </div>
          <v-list dense>
            <v-list-item
              v-for="task in todayTasks"
              :key="'today-' + task.id"
              class="mb-1 rounded"
              style="cursor: pointer"
              @click="openDetail(task)"
            >
              <v-list-item-icon>
                <v-icon
                  :color="
                    priorityColor(task.task ? task.task.priority : 'basic')
                  "
                >
                  mdi-checkbox-blank-circle
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  {{ task.task ? task.task.title : "" }}
                  <v-chip
                    v-if="task.days_delayed > 0"
                    x-small
                    color="deep-orange"
                    dark
                    class="ml-1"
                    >{{ task.days_delayed }} day{{
                      task.days_delayed > 1 ? "s" : ""
                    }}
                    delayed</v-chip
                  >
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{
                    task.task && task.task.scheduled_time
                      ? "at " + task.task.scheduled_time
                      : ""
                  }}
                  {{
                    task.task && task.task.category
                      ? " - " + task.task.category
                      : ""
                  }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn small color="success" @click="completeTask(task)">
                  <v-icon small left>mdi-check</v-icon>
                  Complete
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </div>

        <div
          v-if="popup_tasks.length === 0"
          class="text-center grey--text pa-6"
        >
          No pending tasks. Great job!
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-btn text color="primary" @click="viewAllTasks">
          <v-icon left>mdi-clipboard-list</v-icon>
          View All Tasks
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="grey" dark @click="dismiss">Dismiss</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Inline completion dialog -->
    <task-completion-dialog
      v-model="showCompleteDialog"
      :task="completingTask"
      @completed="onCompleted"
    />

    <!-- Detail Dialog -->
    <task-detail-dialog v-model="showDetailDialog" :task="detailTask" />
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import TaskCompletionDialog from "./TaskCompletionDialog.vue";
import TaskDetailDialog from "./TaskDetailDialog.vue";

export default {
  components: { TaskCompletionDialog, TaskDetailDialog },
  data() {
    return {
      showCompleteDialog: false,
      completingTask: null,
      showDetailDialog: false,
      detailTask: null,
    };
  },
  computed: {
    ...mapGetters({
      show_popup: "reminder/show_popup",
      popup_tasks: "reminder/popup_tasks",
      user: "auth/user",
    }),
    todayTasks() {
      const today = new Date().toISOString().substr(0, 10);
      return this.popup_tasks.filter(
        (t) => t.log_date && t.log_date.substr(0, 10) === today
      );
    },
    overdueTasks() {
      const today = new Date().toISOString().substr(0, 10);
      // Recurring tasks are already consolidated into today's entry with days_delayed,
      // so only show non-recurring overdue tasks here
      const todayTaskIds = this.todayTasks
        .filter(
          (t) =>
            t.task &&
            t.task.recurrence_type &&
            t.task.recurrence_type !== "none"
        )
        .map((t) => t.task_id);
      return this.popup_tasks.filter(
        (t) =>
          t.log_date &&
          t.log_date.substr(0, 10) < today &&
          !todayTaskIds.includes(t.task_id)
      );
    },
  },
  methods: {
    ...mapActions({
      dismiss_popup: "reminder/dismiss_popup",
      get_pending_tasks: "reminder/get_pending_tasks",
    }),
    priorityColor(p) {
      return p === "very_urgent" ? "red" : p === "urgent" ? "orange" : "blue";
    },
    dismiss() {
      this.dismiss_popup();
    },
    viewAllTasks() {
      this.dismiss_popup();
      if (this.can("route.task_management")) {
        this.$router.push("/task_management").catch(() => {});
      }
    },
    openDetail(task) {
      this.detailTask = task;
      this.showDetailDialog = true;
    },
    completeTask(task) {
      this.completingTask = task;
      this.showCompleteDialog = true;
    },
    onCompleted() {
      this.get_pending_tasks();
    },
  },
};
</script>
